import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { notifications } from '@mantine/notifications'
import {
  downloadTemplateChecklists,
  useTemplateImportar,
  type TemplateImportLinha,
  type TemplateImportResultado,
} from '../../hooks/useTemplates'
import { extractApiError } from '../../lib/extractApiError'
import styles from '../_shared/CrudPage.module.css'

const ACAO_LABEL: Record<string, { label: string; bg: string; color: string }> = {
  criar: { label: 'Criar', bg: '#E8F5E9', color: '#2E7D32' },
  ignorar_duplicado: { label: 'Ignorar (duplicado)', bg: '#FFF8E1', color: '#F9A825' },
  erro: { label: 'Erro', bg: '#FFEBEE', color: '#C62828' },
}

function LinhasTabela({
  titulo,
  linhas,
  testIdPrefix,
}: {
  titulo: string
  linhas: TemplateImportLinha[]
  testIdPrefix: string
}) {
  if (linhas.length === 0) return null
  return (
    <div style={{ marginBottom: '1rem' }}>
      <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>{titulo}</h3>
      <div style={{ maxHeight: 250, overflow: 'auto', border: '1px solid #E0E0E0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead style={{ background: '#F5F5F5', position: 'sticky', top: 0 }}>
            <tr>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Linha</th>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Ação</th>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Resumo</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => {
              const meta = ACAO_LABEL[l.acao]
              const ehErro = l.acao === 'erro'
              const d = l.dados as Record<string, unknown>
              const resumo = ehErro
                ? (l.erro ?? '(sem detalhe)')
                : (d.nome
                    ? `${d.nome} (${d.tipo_os ?? ''})`
                    : `${d.template_nome ?? ''} #${d.ordem ?? ''} ${d.pergunta ? '— ' + String(d.pergunta).slice(0, 60) : ''}`)
              return (
                <tr
                  key={`${l.aba}-${l.linha}`}
                  style={{
                    borderTop: '1px solid #EEE',
                    background: ehErro ? '#FFEBEE' : undefined,
                  }}
                  data-testid={`${testIdPrefix}-${l.linha}`}
                >
                  <td style={{ padding: '0.5rem' }}>{l.linha}</td>
                  <td style={{ padding: '0.5rem' }}>
                    <span
                      style={{
                        background: meta.bg,
                        color: meta.color,
                        padding: '0.15rem 0.5rem',
                        borderRadius: 999,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      {meta.label}
                    </span>
                  </td>
                  <td
                    style={{
                      padding: '0.5rem',
                      color: ehErro ? '#C62828' : undefined,
                      fontWeight: ehErro ? 500 : 400,
                    }}
                  >
                    {resumo}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function TemplateImportarPage() {
  const navigate = useNavigate()
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [preview, setPreview] = useState<TemplateImportResultado | null>(null)
  const importar = useTemplateImportar()

  async function handlePreview() {
    if (!arquivo) {
      notifications.show({ color: 'red', message: 'Selecione um arquivo .xlsx.' })
      return
    }
    try {
      const r = await importar.mutateAsync({ arquivo, dry_run: true })
      setPreview(r)
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha no preview',
        message: extractApiError(err, 'Erro ao processar planilha — verifique o formato.'),
      })
    }
  }

  async function handleConfirmar() {
    if (!arquivo || !preview) return
    if (preview.templates.a_criar === 0) {
      notifications.show({ color: 'orange', message: 'Nenhum template para criar.' })
      return
    }
    if (
      !confirm(
        `Confirmar importação de ${preview.templates.a_criar} template(s) e ${preview.itens.a_criar} item(ns)? Esta ação é irreversível.`,
      )
    )
      return
    try {
      const r = await importar.mutateAsync({ arquivo, dry_run: false })
      notifications.show({
        color: 'green',
        message: `${r.commit?.templates_criados ?? 0} template(s) e ${r.commit?.itens_criados ?? 0} item(ns) criados.`,
      })
      navigate('/checklists/templates')
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha no commit',
        message: extractApiError(err, 'Erro no commit. Tente analisar de novo.'),
      })
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <button
            className={styles.backBtn}
            onClick={() => navigate('/checklists/templates')}
            type="button"
          >
            ← Voltar
          </button>
          <h1 className={styles.title}>Importar templates de checklist</h1>
        </div>
        <button
          type="button"
          onClick={async () => {
            try {
              await downloadTemplateChecklists()
              notifications.show({ color: 'green', message: 'Template baixado.' })
            } catch (e) {
              notifications.show({
                color: 'red',
                title: 'Não foi possível baixar o template',
                message: extractApiError(e),
              })
            }
          }}
          style={{
            padding: '0.5rem 1rem',
            background: 'white',
            color: '#1565C0',
            border: '1px solid #1565C0',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          📥 Baixar template
        </button>
      </header>

      <section
        style={{
          background: 'white',
          padding: '1.5rem',
          borderRadius: 8,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>1. Estrutura da planilha</h2>
        <ul style={{ marginTop: 0, color: '#555' }}>
          <li>
            <strong>Aba 1 "Templates"</strong>: NOME, TIPO_OS (preventivo/qai/...),
            TIPO_EQUIPAMENTO (opcional), DESCRIÇÃO, ATIVO
          </li>
          <li>
            <strong>Aba 2 "Itens"</strong>: TEMPLATE (ref. ao NOME), ORDEM,
            PERGUNTA, TIPO_RESPOSTA (sim_nao/numerico/texto), OBRIGATORIO,
            REQUER_EVIDENCIA, UNIDADE, VALOR_MIN, VALOR_MAX, AJUDA
          </li>
          <li>Itens com TEMPLATE ainda não cadastrado vão pra erro</li>
          <li>Use o template oficial pra ver exemplos e instruções</li>
        </ul>

        <h2 style={{ marginTop: '1.5rem', fontSize: '1rem' }}>2. Selecione o arquivo</h2>
        <input
          type="file"
          accept=".xlsx,.xls"
          data-testid="templates-import-arquivo"
          onChange={(e) => {
            setArquivo(e.target.files?.[0] ?? null)
            setPreview(null)
          }}
        />
        {arquivo && (
          <p style={{ margin: '0.5rem 0', color: '#666', fontSize: '0.85rem' }}>
            {arquivo.name} • {(arquivo.size / 1024).toFixed(1)} KB
          </p>
        )}

        <div style={{ marginTop: '1rem' }}>
          <button
            type="button"
            data-testid="templates-import-analisar"
            onClick={handlePreview}
            disabled={!arquivo || importar.isPending}
            style={{
              padding: '0.5rem 1.25rem',
              background: '#1565C0',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {importar.isPending && !preview ? 'Analisando...' : 'Analisar planilha'}
          </button>
        </div>
      </section>

      {preview && (
        <section
          style={{
            background: 'white',
            padding: '1.5rem',
            borderRadius: 8,
            marginTop: '1rem',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          }}
        >
          <h2 style={{ marginTop: 0, fontSize: '1rem' }}>3. Preview</h2>

          <div
            style={{
              display: 'flex',
              gap: '2rem',
              padding: '1rem',
              background: '#FAFAFA',
              borderRadius: 4,
              marginBottom: '1rem',
              flexWrap: 'wrap',
            }}
          >
            <div>
              <strong style={{ fontSize: '0.85rem', color: '#666' }}>TEMPLATES</strong>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem' }}>
                <span data-testid="templates-preview-criar" style={{ color: '#2E7D32' }}>
                  <strong>{preview.templates.a_criar}</strong> a criar
                </span>
                <span data-testid="templates-preview-dup" style={{ color: '#F9A825' }}>
                  <strong>{preview.templates.duplicados}</strong> dup
                </span>
                <span data-testid="templates-preview-erros" style={{ color: '#C62828' }}>
                  <strong>{preview.templates.erros}</strong> erros
                </span>
              </div>
            </div>
            <div>
              <strong style={{ fontSize: '0.85rem', color: '#666' }}>ITENS</strong>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem' }}>
                <span data-testid="itens-preview-criar" style={{ color: '#2E7D32' }}>
                  <strong>{preview.itens.a_criar}</strong> a criar
                </span>
                <span data-testid="itens-preview-erros" style={{ color: '#C62828' }}>
                  <strong>{preview.itens.erros}</strong> erros
                </span>
              </div>
            </div>
          </div>

          <LinhasTabela
            titulo="Aba Templates"
            linhas={preview.templates.linhas}
            testIdPrefix="tpl-linha"
          />
          <LinhasTabela
            titulo="Aba Itens"
            linhas={preview.itens.linhas}
            testIdPrefix="item-linha"
          />

          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              data-testid="templates-import-confirmar"
              onClick={handleConfirmar}
              disabled={preview.templates.a_criar === 0 || importar.isPending}
              style={{
                padding: '0.5rem 1.25rem',
                background: '#2E7D32',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              {importar.isPending
                ? 'Importando...'
                : `✓ Confirmar (${preview.templates.a_criar} templates / ${preview.itens.a_criar} itens)`}
            </button>
            <button
              type="button"
              onClick={() => {
                setPreview(null)
                setArquivo(null)
              }}
              style={{
                padding: '0.5rem 1.25rem',
                background: 'transparent',
                color: '#666',
                border: '1px solid #BBB',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              Cancelar / Re-analisar
            </button>
          </div>
        </section>
      )}
    </div>
  )
}
